import { startTransition, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { staffingWeeklyPath } from "../../../utils/staffingRoutes";
import { closeGapsSheetIfCurrent, type GapsSheetIntent, getGapsSheetIntent, openGapsSheet, replaceGapsSheetIfCurrent } from "./useGapsSheet";

/** An intent for one of the HOME's sheets — the ones that carry the day they act on. */
type HomeDayIntent = Extract<GapsSheetIntent, { dayISO: string }>;

type OpenOnGapsDay = {
	/** Opens a home sheet on its own day — from the home itself or from another gaps page. */
	openOnDay: (intent: HomeDayIntent) => void;
	/**
	 * Hands the sheet `from` on to a home sheet on its day, only while `from` is still the one up (an approval that
	 * finished after the user closed it, or moved to another sheet, hands on to nothing). `false` when it did not.
	 */
	handOnToDay: (from: GapsSheetIntent, next: HomeDayIntent) => boolean;
	/** Closes `from` and moves to a day with no sheet — again only while `from` is still up. */
	leaveToDay: (from: GapsSheetIntent, dayISO: string) => void;
};

/**
 * Opens one of the HOME's sheets on a given staffing day — from the home itself or from another gaps page.
 *
 * The intent is written first and outlives the navigation (see `useGapsSheet`), so the home that mounts, or re-renders on
 * the new day, finds it and opens the sheet once that day is loaded. Used where a sheet off the home hands on to a home
 * sheet about a specific day: an approval from Open Shifts continues in the assign-unit sheet on the request's day (P3-6),
 * as the classic approve navigates to that day; "Add someone directly" opens the add sheet there (P3-10). `leaveToDay` is
 * the same move with no sheet — an approval that created nothing to place (a swap) still lands on its day.
 */
export const useOpenOnGapsDay = (): OpenOnGapsDay => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;

	const goToDay = useCallback(
		(dayISO: string): void => {
			const destination = staffingWeeklyPath(departmentId, dayISO);
			if (pathname === destination) return;
			// The home's render is the heavy half and nothing here waits on it (the bottom bar's own reasoning).
			startTransition(() => navigate(destination));
		},
		[departmentId, navigate, pathname],
	);

	const openOnDay = useCallback(
		(intent: HomeDayIntent): void => {
			openGapsSheet(intent);
			goToDay(intent.dayISO);
		},
		[goToDay],
	);

	const handOnToDay = useCallback(
		(from: GapsSheetIntent, next: HomeDayIntent): boolean => {
			if (!replaceGapsSheetIfCurrent(from, next)) return false;
			goToDay(next.dayISO);
			return true;
		},
		[goToDay],
	);

	const leaveToDay = useCallback(
		(from: GapsSheetIntent, dayISO: string): void => {
			if (getGapsSheetIntent() !== from) return;
			closeGapsSheetIfCurrent(from);
			goToDay(dayISO);
		},
		[goToDay],
	);

	return { openOnDay, handOnToDay, leaveToDay };
};
